import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";

import type { VatFeeProfile } from "../../app-main/selectors/fee-profile.selectors";
import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { TAX_DETAILS_STATUS } from "../../app-payments/TaxDetails/TaxDetails.consts";
import { getTaxDetailsStatus } from "../../app-payments/TaxDetails/TaxDetails.utils";
import { useGetVatFeeProfilesQuery } from "../../services/fees.service";

export type UseHostHubVatAttentionResult = {
  /** Whether the VAT attention item should currently be rendered. */
  isVisible: boolean;
  isLoading: boolean;
};

/**
 * Whether any VAT registration needs the host's attention. Reads the same
 * status as the payments banners, so the two can never disagree.
 */
const hasVatNeedingAttention = (vatProfiles: VatFeeProfile[]) =>
  vatProfiles.some(
    (vatProfile) =>
      getTaxDetailsStatus(vatProfile) === TAX_DETAILS_STATUS.invalid,
  );

/**
 * Shows the VAT attention item when the authority rejected a VAT ID. A failed
 * read keeps it hidden rather than warning a host whose ID may be fine.
 */
const useHostHubVatAttention = (): UseHostHubVatAttentionResult => {
  const ssoId = useSelector(selectProfileSSOid);

  const { data: vatProfiles, isLoading } = useGetVatFeeProfilesQuery(
    ssoId ? { ssoId } : skipToken,
  );

  return {
    isVisible: hasVatNeedingAttention(vatProfiles ?? []),
    isLoading,
  };
};

export default useHostHubVatAttention;
